import { supabase } from './supabase';
import { collectClientSignals } from './formSignals';
import { getConversionAttribution } from './growthIntel/tracker';

export type FormSubmissionKind =
  | 'contact'
  | 'request_consultants'
  | 'newsletter'
  | 'job_application'
  | 'other';

export interface SubmitFormInput {
  form: FormSubmissionKind;
  name?: string | null;
  email?: string | null;
  company?: string | null;
  phone?: string | null;
  message?: string | null;
  data?: Record<string, unknown>;
  page?: string | null;
  /** Optional attribution source, e.g. "resource_download". */
  source?: string | null;
  /** Value of the honeypot field — must be empty for a real submission. */
  honeypot?: string;
  /** Milliseconds the user spent on the form before submitting. */
  elapsedMs?: number;
  /** Cloudflare Turnstile token (verified server-side when configured). */
  turnstileToken?: string;
  /** Client-observed IP (optional — the server also derives it from headers). */
  ip?: string;
  // Job application specifics
  jobId?: string;
  jobTitle?: string;
  resumePath?: string;
  linkedinUrl?: string;
  coverLetter?: string;
}

export interface FormSubmissionResult {
  ok: boolean;
  error?: string;
  id?: string | null;
  /** Server-generated human reference (e.g. CEN-XXXX) when available. */
  reference?: string | null;
  deliveryStatus?: string;
  /** Gated file (site_forms download) — only returned after a successful save. */
  fileUrl?: string | null;
  fileName?: string | null;
  /** True when the server rejected the submission with HTTP 429 (rate limit). */
  rateLimited?: boolean;
}

/** Window event dispatched whenever a form submission is rate-limited (HTTP 429). */
export const RATE_LIMITED_EVENT = 'cendien:rate-limited';

function emitRateLimited(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(RATE_LIMITED_EVENT));
}

/** Detects a rate-limit response from either the HTTP status or the message body. */
function isRateLimited(status: number, message: string): boolean {
  if (status === 429) return true;
  return /too many submissions|rate limit|too many requests/i.test(message);
}

/**
 * The single authoritative path for every public form submission.
 *
 * Calls the `submit-form` edge function, which (server-side) validates the
 * input, checks the honeypot + time-on-form, rate-limits by hashed IP, inserts
 * the row with the service role, sends the Teams notification + Resend email,
 * and records delivery status.
 *
 * Success is only reported once the row is persisted.
 */
export async function submitForm(input: SubmitFormInput): Promise<FormSubmissionResult> {
  try {
    // Attach the client-observed signals (screen, journey, timings, paste/typed,
    // UTM, session id). The server derives IP/geo itself and trusts none of this
    // for security — it is enrichment only.
    const client = collectClientSignals(input.page || undefined);
    const body: Record<string, unknown> = { ...input, client, attribution: getConversionAttribution() };
    const { data, error } = await supabase.functions.invoke('submit-form', { body });
    if (error) {
      let msg = error.message || 'We could not save your submission. Please try again.';
      let status = 0;
      try {
        const ctx = (error as unknown as { context?: Response }).context;
        if (ctx) {
          status = ctx.status;
          if (typeof ctx.json === 'function') {
            const parsed = await ctx.json();
            if (parsed?.error) msg = parsed.error as string;
          }
        }
      } catch {
        /* fall back to the default message */
      }
      if (isRateLimited(status, msg)) {
        emitRateLimited();
        return { ok: false, rateLimited: true, error: msg };
      }
      return { ok: false, error: msg };
    }
    if (!data || data.ok !== true) {
      return {
        ok: false,
        error:
          (data && (data.error as string)) ||
          'We could not save your submission. Please try again.',
      };
    }
    return {
      ok: true, id: (data.id as string) ?? null, reference: (data.reference as string) ?? null, deliveryStatus: data.delivery_status as string,
      fileUrl: (data.file_url as string) ?? null, fileName: (data.file_name as string) ?? null,
    };
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error
          ? err.message
          : 'We could not save your submission. Please try again.',
    };
  }
}

export interface FormSubmissionInput {
  form: FormSubmissionKind;
  name?: string | null;
  email?: string | null;
  company?: string | null;
  phone?: string | null;
  message?: string | null;
  data?: Record<string, unknown>;
  page?: string | null;
  /** Cloudflare Turnstile token (verified server-side when configured). */
  turnstileToken?: string;
}

/**
 * Back-compat wrapper: routes older callers (newsletter signups) through the
 * same authoritative `submit-form` path.
 */
export async function saveFormSubmission(
  input: FormSubmissionInput,
): Promise<FormSubmissionResult> {
  return submitForm({
    form: input.form,
    name: input.name,
    email: input.email,
    company: input.company,
    phone: input.phone,
    message: input.message,
    data: input.data,
    page: input.page,
    turnstileToken: input.turnstileToken,
  });
}

/**
 * Returns true when a honeypot field has been filled in, which means the
 * submission is almost certainly a bot. Callers should silently show the same
 * generic success state without submitting.
 */
export function isHoneypotTripped(form: HTMLFormElement, fieldName = 'website_alt'): boolean {
  try {
    const value = new FormData(form).get(fieldName);
    return typeof value === 'string' && value.trim().length > 0;
  } catch {
    return false;
  }
}

/** Minimum time (ms) a real visitor should spend before submitting a form. */
export const MIN_FORM_FILL_MS = 3000;